import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { isHotelPackage } from 'lib/offer/offerTypes'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import {
  arePropertyRatesLoading,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AlwaysOnRateOption from '../AlwaysOn/AlwaysOnRateOption'
import BedbankRoomOptionRate from '../Bedbank/BedbankRoomOptionRate'
import PropertyRateOption from './PropertyRateOption'
import type { RateView } from './singlePropertyUtils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  propertyRoom: App.HotelPackage | App.BedbankPackage;
  availableRooms: number | undefined;
  displayRates: Array<RateView>;
  roomFitsCapacity: boolean;
  datesAvailable: boolean;
  checkIn?: string;
  rooms: Array<App.Occupants>;
  duration?: number;
  airport?: App.Airport;
  roomListId: string;
  roomPosition: number;
  availableForPurchase?: boolean;
  isLoading: boolean;
}

function PropertyRoomOption(props: Props) {
  const {
    offer,
    bedbankOffer,
    propertyRoom,
    availableRooms = 0,
    displayRates,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    rooms,
    duration,
    roomListId,
    roomPosition,
    availableForPurchase,
    isLoading,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  // trust me bro, it's a hotel package
  const isLpcPackage = isHotelPackage(propertyRoom)
  const opaqueRoomId = isLpcPackage ? propertyRoom.roomType.id : propertyRoom.roomId
  const roomType = isLpcPackage ? propertyRoom.roomType : undefined
  const [showingMoreRates, showMoreRates] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const initialRatesToShow = 3

  const checkAvailability = !checkIn || !rooms

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (isLoading) return undefined
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      if (!offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return 'available-on-dates'
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (offer.property?.useDynamicOccupancy) {
        if (!checkIn) return 'available-on-dates'
        if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
      }
    }
  }, [availableForPurchase, checkAvailability, checkIn, datesAvailable, isLoading, offer, roomFitsCapacity])

  const stayPayTier = useMemo(() => {
    return displayRates.map(({ lpcRate: view }) => {
      if (view && view.hotelAvailable && view.package.stayPay) {
        return view.package.stayPay.find(tier => tier.stayNights === duration && tier.freeNights > 0)
      }
    }).find(Boolean)
  }, [displayRates, duration])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, roomType])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter(rooms ? 'checkin' : 'capacity')
  }, [rooms, toggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayRates]) // regenerate id if packages change

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner
        kind="promotional-primary"
        icon={<LineInfoCircleIcon />}
        description={<>
          <b>Special offer:</b> You get <Plural singular="night" count={stayPayTier.freeNights} withCount />{' '}
          free when staying <Plural singular="night" count={stayPayTier.stayNights} withCount /> on select packages.
        </>}
      />}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        <ScrollAnchor id={opaqueRoomId} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={propertyRoom}>
          <AccommodationRoomTileImage
            roomPackage={propertyRoom}
            // Hide the bookmark button if there's no LPC package, as otherwise we'd wind up with a
            // mix of reservation _and_ bedbank room IDs. In the future, we'd want to migrate to
            // using accommodation room IDs.
            showBookmark={isLpcPackage}
          />
          <AccommodationRoomTileDetails
            roomPackage={propertyRoom}
            urgencyLabels={<>
              {roomType && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
              {checkIn && <RoomsRemainingLabel roomCount={availableRooms} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && roomType && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          >
            <AccommodationRoomTileActions
              roomPackage={propertyRoom}
              state={actionsState}
              isWalled={false}
              onAddAnotherUnitClick={handleAddAnotherUnitClick}
              onChangeDatesClick={handleChangeDatesClick}
              onCheckAvailabilityClick={handleCheckAvailabilityClick}
            />
          </AccommodationRoomTileDetails>
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {take(displayRates, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => {
            if (view.lpcRate) {
              return <AlwaysOnRateOption
                key={view.lpcRate.package.uniqueKey}
                offer={offer}
                optionNumber={index + 1}
                packageView={view.lpcRate}
                showOptionLabel={displayRates.length > 1}
              />
            } else if (view.bedbankRate && bedbankOffer) {
              return <BedbankRoomOptionRate
                key={view.bedbankRate.rate.id}
                offer={bedbankOffer}
                packageView={view.bedbankRate.view}
                rate={view.bedbankRate.rate}
                optionNumber={index + 1}
                roomPosition={roomPosition}
                roomListId={roomListId}
                rateListId={rateListId}
                showOptionLabel={displayRates.length > 1}
              />
            } else if (view.propertyRate) {
              return <PropertyRateOption
                key={view.propertyRate.rateId}
                lpcOffer={offer}
                bedbankOffer={bedbankOffer}
                rate={view.propertyRate}
                optionNumber={index + 1}
                showOptionLabel={displayRates.length > 1}
                roomListId={roomListId}
                roomPosition={roomPosition}
                rateListId={rateListId}
              />
            }
            return null
          })}
          {isLoading && <LoadingIndicator inline />}
          {displayRates.length > initialRatesToShow && <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
              {showingMoreRates ? 'Show less options' : 'Show more options'}
            </SmallToggleButton>
          </ShowMoreContent>}
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  airport: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOption, preventRenderWithOpenFilter)
